import { Action,PayloadAction } from '@reduxjs/toolkit';
import { call, delay,fork, put, select, take, takeLatest } from 'redux-saga/effects';

import { selectUser } from '@/entities/user';
import { userActions } from '@/entities/user';
import type { CurrencyCode } from '@/shared/lib/currency';
import { twa } from '@/shared/lib/telegram/twa';

import { rollsApi } from '../api/rollsApi';
import { selectHistoryHasMore, selectHistoryLoading,selectHistoryPage } from './historySelectors';
import { selectHistoryFetchedAt } from './historySelectors';
import { historyActions } from './historySlice';
import {
  selectIsRollsAutoplayActive,
  selectRollsAutoplay,
  selectRollsSelectedBalance,
  selectRollsSelectedCurrency,
  selectRollsStatus,
} from './selectors';
import { rollsActions } from './slice';
import { PaginatedHistory, RollsRound, RollsStatus } from './types';

// ── Place bet via API ──────────────────────────────────────────────────

type BetPayload = { amount: number; currency: CurrencyCode; giftId?: string };

function* placeBetSaga(action: PayloadAction<BetPayload>): Generator {
  const user = (yield select(selectUser)) as ReturnType<typeof selectUser>;
  if (!user) return;
  const { amount, currency, giftId } = action.payload;
  // Gift bets are settled server-side from the chosen gift, not the TON balance.
  if (!giftId) {
    const balance = (yield select(selectRollsSelectedBalance)) as number;
    if (amount > balance) return;
  }

  try {
    const round = (yield call(rollsApi.placeBet, amount, currency, giftId)) as RollsRound;
    yield put(rollsActions.setRound(round));
    if (!giftId) {
      yield put(userActions.updateBalanceByCurrency({ currency, amount: -amount }));
    }
    twa.hapticFeedback.notificationOccurred('success');
  } catch {
    twa.hapticFeedback.notificationOccurred('error');
  }
}

// ── Add bet (same API, backend handles existing player) ────────────────

function* addBetSaga(action: PayloadAction<BetPayload>): Generator {
  const user = (yield select(selectUser)) as ReturnType<typeof selectUser>;
  if (!user) return;
  const { amount, currency, giftId } = action.payload;
  if (!giftId) {
    const balance = (yield select(selectRollsSelectedBalance)) as number;
    if (amount > balance) return;
  }

  try {
    const round = (yield call(rollsApi.placeBet, amount, currency, giftId)) as RollsRound;
    yield put(rollsActions.setRound(round));
    if (!giftId) {
      yield put(userActions.updateBalanceByCurrency({ currency, amount: -amount }));
    }
    twa.hapticFeedback.selectionChanged();
  } catch {
    twa.hapticFeedback.notificationOccurred('error');
  }
}

// ── History sagas ──────────────────────────────────────────────────────

function* fetchHistorySaga(): Generator {
  try {
    const result = (yield call(rollsApi.getHistory, 1, 20)) as PaginatedHistory;
    yield put(historyActions.setHistory(result));
  } catch {
    // ignore
  }
}

function* fetchHistoryNextPageSaga(): Generator {
  const hasMore = (yield select(selectHistoryHasMore)) as boolean;
  const loading = (yield select(selectHistoryLoading)) as boolean;

  if (!hasMore || loading) return;

  const page = (yield select(selectHistoryPage)) as number;

  yield put(historyActions.setLoading(true));
  try {
    const result = (yield call(rollsApi.getHistory, page + 1, 20)) as PaginatedHistory;
    yield put(historyActions.appendHistory(result));
  } catch {
    yield put(historyActions.setLoading(false));
  }
}

// ── Autoplay saga ──────────────────────────────────────────────────────

function* rollsAutoplaySaga(): Generator {
  while (true) {
    // Trigger on new round OR immediately when user starts autoplay mid-wait
    yield take([rollsActions.wsNewRound.type, rollsActions.startAutoplay.type]);

    const isActive = (yield select(selectIsRollsAutoplayActive)) as boolean;
    if (!isActive) continue;

    // If triggered by startAutoplay, only proceed if game is in a bettable phase
    const status = (yield select(selectRollsStatus)) as ReturnType<typeof selectRollsStatus>;
    if (status !== RollsStatus.WAITING && status !== RollsStatus.COUNTDOWN) continue;

    yield delay(300 + Math.floor(Math.random() * 700));

    const stillActive = (yield select(selectIsRollsAutoplayActive)) as boolean;
    if (!stillActive) continue;

    const balance = (yield select(selectRollsSelectedBalance)) as number;
    const currency = (yield select(selectRollsSelectedCurrency)) as CurrencyCode;
    const { betAmount, numberOfGames, gamesPlayed } = (yield select(
      selectRollsAutoplay,
    )) as ReturnType<typeof selectRollsAutoplay>;

    if (balance < betAmount) {
      yield put(rollsActions.stopAutoplay());
      twa.hapticFeedback.notificationOccurred('error');
      continue;
    }

    yield put(rollsActions.placeBet({ amount: betAmount, currency }));
    yield put(rollsActions.recordAutoplayGame());

    // Stop once the configured number of games has been reached.
    if (gamesPlayed + 1 >= numberOfGames) {
      yield put(rollsActions.stopAutoplay());
    }
  }
}

const HISTORY_TTL_MS = 60_000;

function* fetchIfStaleSaga(): Generator {
  const fetchedAt = (yield select(selectHistoryFetchedAt)) as number | null;
  const isStale = fetchedAt === null || Date.now() - fetchedAt > HISTORY_TTL_MS;
  if (isStale) yield call(fetchHistorySaga);
}

// ── Init: fetch current round + history from API ───────────────────────

function* initRollsSaga(): Generator {
  try {
    const round = (yield call(rollsApi.getCurrentRound)) as RollsRound;
    yield put(rollsActions.setRound(round));
  } catch {
    // backend not available yet, WS will sync
  }

  yield call(fetchHistorySaga);
}

// ── Watchers ───────────────────────────────────────────────────────────

export function* rollsWatcher(): Generator<unknown, void, Action> {
  yield takeLatest(rollsActions.placeBet.type, placeBetSaga);
  yield takeLatest(rollsActions.requestAddBet.type, addBetSaga);
  yield takeLatest(historyActions.fetchRequest.type, fetchHistorySaga);
  yield takeLatest(historyActions.fetchIfStale.type, fetchIfStaleSaga);
  yield takeLatest(historyActions.fetchNextPage.type, fetchHistoryNextPageSaga);
  yield fork(rollsAutoplaySaga);
  yield call(initRollsSaga);
}
